import React, { useState } from "react";
import { Box, Button, IconButton, Tooltip, Typography } from "@material-ui/core";
import { Add, Delete, Visibility } from "@material-ui/icons";
import { useFormContext, useWatch } from "react-hook-form";
import { useSortableFieldArray } from "routes/createContent/views/lessons/hooks/useSortableFieldArray";
import { TemplateFormData, TemplateFormExercise } from "../types";
import { MAX_DURATION_WEEKS } from "../constants";
import ExerciseRow from "./ExerciseRow";
import ExercisePickerDialog from "./ExercisePickerDialog";
import ExercisePreviewDialog from "./ExercisePreviewDialog";
import TemplateItemRow from "./TemplateItemRow";

// Exercises are unordered on the template (the v2 payload has no per-exercise
// order/position), so this list is not drag-sortable — just add/remove. Each
// exercise is a single flat row: thumbnail, title, inline controls, actions.
const ExercisesSection: React.VFC<{ readOnly?: boolean }> = ({ readOnly = false }) => {
  const { control } = useFormContext<TemplateFormData>();
  const { fields, ids, append, remove } = useSortableFieldArray("exercises");
  const [pickerOpen, setPickerOpen] = useState(false);
  const [previewId, setPreviewId] = useState<number | null>(null);

  const ongoing = useWatch({ control, name: "ongoing" }) as boolean;
  const durationWeeks = useWatch({ control, name: "duration_weeks" }) as number;
  const maxWeeks = ongoing ? MAX_DURATION_WEEKS : durationWeeks || 1;

  const existingIds = fields.map(f => f.exercise_id as number);

  const onAdd = (exercises: TemplateFormExercise[]) => exercises.forEach(ex => append(ex));

  return (
    <Box mt={3}>
      <Box display="flex" alignItems="center" mb={1}>
        <Typography variant="h6">Exercises</Typography>
        {!readOnly && (
          <Button startIcon={<Add />} size="small" style={{ marginLeft: 12 }} onClick={() => setPickerOpen(true)}>
            Add exercises
          </Button>
        )}
      </Box>

      {fields.length === 0 ? (
        <Typography color="textSecondary">No exercises added yet.</Typography>
      ) : (
        fields.map((f, i) => {
          const title = (f.title as string) || `Exercise ${i + 1}`;
          return (
            <TemplateItemRow key={ids[i]} imageUrl={f.image_url as string | null} title={title}>
              <ExerciseRow
                index={i}
                maxWeeks={maxWeeks}
                readOnly={readOnly}
                defaults={f as unknown as TemplateFormExercise}
              />
              <Box display="flex" alignItems="center" style={{ flexShrink: 0 }}>
                <Tooltip title="Preview">
                  <IconButton
                    size="small"
                    aria-label={`Preview ${title}`}
                    onClick={() => setPreviewId(f.exercise_id as number)}
                  >
                    <Visibility fontSize="small" />
                  </IconButton>
                </Tooltip>
                {!readOnly && (
                  <Tooltip title="Remove">
                    <IconButton size="small" aria-label={`Remove ${title}`} onClick={() => remove(i)}>
                      <Delete fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </TemplateItemRow>
          );
        })
      )}

      <ExercisePickerDialog
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        existingIds={existingIds}
        onAdd={onAdd}
      />
      <ExercisePreviewDialog exerciseId={previewId} onClose={() => setPreviewId(null)} />
    </Box>
  );
};

export default ExercisesSection;
